import { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/routes/routes';
import { OutboundStatus } from '@/infrastructure/StockClient';
import type { StockClient } from '@/infrastructure/StockClient';
import type { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import { useFulfillmentV2Launch } from '@/infrastructure/FulfillmentV2Launch';
import { FulfillmentSound } from '@/pages/fulfillment/components/outboundSound';
import { asciiScanField } from '@/pages/fulfillment/components/asciiScanField';
import { CameraScanField } from '@/pages/fulfillment/components/CameraScanField';
import { ManualStockForm } from './ManualStockForm';

export type MovementDirection = 'inbound' | 'outbound';

export interface MovementFormPaneProps {
  direction: MovementDirection;
  stockClient: StockClient;
  /** Books the outbound on Fulfillment 2.0 once it is launched (SSD-7099), reading the sku's stock beside it. */
  adminClient: Pick<FulfillmentAdminClient, 'adjustStock' | 'listInventory'>;
  operator: string;
  onRecorded: (message: string) => void;
  onFailure: (message: string) => void;
}

const CARRIERS: Record<MovementDirection, string[]> = {
  inbound: ['Coupang', 'Deleo', 'Direct'],
  outbound: ['Deleo', 'YUN', 'Coupang'],
};

const NOTE: Record<MovementDirection, string> = {
  inbound:
    'Record a warehouse inbound directly, without a specific order (return restock · manual inbound). For order-linked inbound, use the row Inbound buttons on View Orders or Order Detail.',
  outbound:
    'Record a warehouse outbound directly. Location applies the sku’s registered location; an outbound beyond the available quantity is refused by the server.',
};

/**
 * The wireframe's Inbound and Outbound forms. Both write a movement against a productId, which the sku
 * lookup resolves first — the warehouse types skus, the stock endpoints speak product ids.
 */
export const MovementFormPane = ({
  direction,
  stockClient,
  adminClient,
  operator,
  onRecorded,
  onFailure,
}: MovementFormPaneProps) => {
  const launch = useFulfillmentV2Launch();
  const label = direction === 'inbound' ? 'Inbound' : 'Outbound';
  const [sku, setSku] = useState('');
  const [qty, setQty] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState(CARRIERS[direction][0] as string);
  const [orderId, setOrderId] = useState('');
  const [busy, setBusy] = useState(false);

  const quantity = Number(qty.trim());
  const ready = sku.trim() !== '' && qty.trim() !== '' && Number.isFinite(quantity) && quantity > 0 && !busy;

  /**
   * Resolves the entered SKU before writing a stock movement.
   * Unknown SKUs and server refusals preserve the form for correction and retry;
   * successful writes clear the draft and notify the inventory page.
   */
  const record = async () => {
    setBusy(true);
    try {
      const product = await stockClient.getProductBySku(sku.trim());
      if (!product) {
        FulfillmentSound.failure();
        onFailure(`Unknown SKU — ${sku.trim()}`);
        return;
      }
      const request = {
        productId: product.productId,
        qty: quantity,
        carrier,
        auditor: operator,
        ...(trackingNumber.trim() ? { trackingNumber: trackingNumber.trim() } : {}),
        ...(orderId.trim() ? { orderId: orderId.trim() } : {}),
      };
      // Inbound uses the existing V2 request/receiving screens, never this legacy write.
      await stockClient.outboundStock({ ...request, status: OutboundStatus.CONFIRMED });
      FulfillmentSound.success();
      setSku('');
      setQty('');
      setTrackingNumber('');
      setOrderId('');
      onRecorded(`${label} recorded`);
    } catch (error) {
      FulfillmentSound.failure();
      // StockClient rethrows the server's sentence, so the toast reads the shortfall rather than a generic line.
      onFailure(error instanceof Error && error.message ? error.message : `Could not record the ${direction}.`);
    } finally {
      setBusy(false);
    }
  };

  if (direction === 'inbound') {
    return (
      <div className="note">
        <p>
          Register an inbound request, then count, assign a shelf and confirm receipt at the scan desk. Registering a
          request alone does not increase stock.
        </p>
        <Link className="btn btn-blue" to={ROUTES.fulfillment.inboundRequests}>
          Register inbound request
        </Link>{' '}
        <Link className="btn btn-line" to={ROUTES.fulfillment.viewOrders}>
          Receive inbound stock
        </Link>
        <p>For order-linked inbound, use the Inbound action on View Orders or Order Detail.</p>
      </div>
    );
  }

  // Once Fulfillment 2.0 is launched V1 refuses this write (410), so the outbound goes to the V2 ledger.
  // Until the switch is read neither form shows, so no V1 write can leave before the answer (SSD-7433);
  // whenever it is off, the V1 form stays exactly as it was.
  if (launch === 'unknown') {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
      </div>
    );
  }
  if (launch === 'on') {
    return (
      <ManualStockForm
        adminClient={adminClient}
        stockClient={stockClient}
        onRecorded={onRecorded}
        onFailure={onFailure}
      />
    );
  }

  return (
    <div>
      <div className="form-note">{NOTE[direction]}</div>
      <div className="formgrid">
        <div className="fld">
          <label htmlFor={`${direction}-sku`}>SKU *</label>
          <CameraScanField label={`Scan ${label} SKU with camera`}>
            <input
              id={`${direction}-sku`}
              aria-label={`${label} SKU`}
              placeholder="100004819"
              value={sku}
              onChange={(event) => setSku(event.target.value)}
            />
          </CameraScanField>
        </div>
        <div className="fld">
          <label htmlFor={`${direction}-qty`}>Quantity *</label>
          <input
            id={`${direction}-qty`}
            aria-label={`${label} quantity`}
            type="number"
            placeholder="0"
            value={qty}
            onChange={(event) => setQty(event.target.value)}
          />
        </div>
        <div className="fld">
          <label htmlFor={`${direction}-tracking`}>Tracking No</label>
          <CameraScanField label={`Scan ${label} tracking number with camera`}>
            <input
              id={`${direction}-tracking`}
              aria-label={`${label} tracking number`}
              placeholder="Optional"
              value={trackingNumber}
              {...asciiScanField(setTrackingNumber)}
            />
          </CameraScanField>
        </div>
        <div className="fld">
          <label htmlFor={`${direction}-carrier`}>Carrier</label>
          <select
            id={`${direction}-carrier`}
            aria-label={`${label} carrier`}
            value={carrier}
            onChange={(event) => setCarrier(event.target.value)}
          >
            {CARRIERS[direction].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="fld">
          <label htmlFor={`${direction}-order`}>Order ID (optional)</label>
          <input
            id={`${direction}-order`}
            aria-label={`${label} order ID`}
            placeholder="If linked"
            value={orderId}
            onChange={(event) => setOrderId(event.target.value)}
          />
        </div>
      </div>
      <button type="button" className="btn btn-blue" disabled={!ready} onClick={() => void record()}>
        Record {label}
      </button>
    </div>
  );
};
